import { Plus, Trash2 } from 'lucide-react';

const OPERATORS = [
  { value: '>', label: '>' },
  { value: '>=', label: '\u2265' },
  { value: '<', label: '<' },
  { value: '<=', label: '\u2264' },
  { value: '==', label: '=' },
  { value: '!=', label: '\u2260' },
];

const COMMON_ATTRIBUTES = [
  'AbsenceDays',
  'Duration',
  'AbsenceType',
  'AbsencePattern',
  'HoursReported',
  'Amount',
  'Salary',
  'PercentIncrease',
  'GradeId',
  'DepartmentId',
];

function emptyCondition() {
  return { description: '', attribute: '', operator: '>', value: '' };
}

/** Build datalist options from registered transaction attributes (fallback to common ones). */
function buildAttributeOptions(attributes) {
  if (Array.isArray(attributes) && attributes.length) {
    return attributes
      .map((a) => {
        const value = a.factType || a.id || a.column || a.label;
        if (!value) return null;
        const labelParts = [a.label || a.factType || a.id, a.tableColumn].filter(Boolean);
        return { value, label: labelParts.join(' \u00b7 ') };
      })
      .filter(Boolean);
  }
  return COMMON_ATTRIBUTES.map((a) => ({ value: a, label: a }));
}

/**
 * Editable IF conditions for a draft design.
 * conditions: Array<{ description, attribute, operator, value }> (AND-joined).
 * attributes: optional registered transaction attributes to populate the attribute picker.
 */
export default function RuleConditionsEditor({
  conditions = [],
  onChange,
  disabled = false,
  attributes = [],
}) {
  const rows = conditions.length ? conditions : [emptyCondition()];
  const attrOptions = buildAttributeOptions(attributes);

  const update = (idx, patch) => {
    if (disabled) return;
    const next = rows.map((c, i) => (i === idx ? { ...c, ...patch } : c));
    onChange(next);
  };

  const addRow = () => {
    if (disabled) return;
    onChange([...rows, emptyCondition()]);
  };

  const removeRow = (idx) => {
    if (disabled) return;
    const next = rows.filter((_, i) => i !== idx);
    onChange(next.length ? next : [emptyCondition()]);
  };

  return (
    <div className="space-y-2">
      <datalist id="rule-condition-attributes">
        {attrOptions.map((a) => (
          <option key={a.value} value={a.value}>
            {a.label}
          </option>
        ))}
      </datalist>
      {rows.map((cond, idx) => (
        <div
          key={idx}
          className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 p-2"
        >
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            {idx === 0 ? 'IF' : 'AND'}
          </span>
          <input
            type="text"
            list="rule-condition-attributes"
            value={cond.attribute || ''}
            onChange={(e) => update(idx, { attribute: e.target.value })}
            placeholder="Attribute (e.g. AbsenceDays)"
            disabled={disabled}
            className="min-w-[10rem] flex-1 rounded border border-slate-300 px-2 py-1 text-xs"
          />
          <select
            value={cond.operator || '>'}
            onChange={(e) => update(idx, { operator: e.target.value })}
            disabled={disabled}
            className="rounded border border-slate-300 px-2 py-1 text-xs"
          >
            {OPERATORS.map((op) => (
              <option key={op.value} value={op.value}>
                {op.label}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={cond.value || ''}
            onChange={(e) => update(idx, { value: e.target.value })}
            placeholder="Value"
            disabled={disabled}
            className="w-24 rounded border border-slate-300 px-2 py-1 text-xs"
          />
          <button
            type="button"
            onClick={() => removeRow(idx)}
            disabled={disabled}
            title="Remove condition"
            className="rounded p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-40"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addRow}
        disabled={disabled}
        className="inline-flex items-center gap-1 rounded-lg border border-dashed border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-cyan-400 hover:text-cyan-700 disabled:opacity-40"
      >
        <Plus className="h-3.5 w-3.5" /> Add condition
      </button>
    </div>
  );
}
